import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, CheckCircle, Lock, Heart, ExternalLink, ChevronRight } from '../icons';
import { useAuth } from '../context/AuthContext';
import { getMyReviewInfo, submitWeddingReview } from '../services/api';
import { toast } from 'react-toastify';

function StarPicker({ value, onChange, size = 28 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '4px', cursor: 'pointer' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          size={size}
          fill={(hovered || value) >= n ? '#f59e0b' : 'none'}
          color={(hovered || value) >= n ? '#f59e0b' : '#d1d5db'}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(n)}
          style={{ transition: 'color 0.1s, fill 0.1s' }}
        />
      ))}
    </div>
  );
}

const RATING_LABELS = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very Good', 5: 'Excellent' };

function VendorReviewCard({ vendor, onSaved }) {
  const [rating, setRating] = useState(vendor.existing_rating || 0);
  const [title, setTitle] = useState(vendor.existing_title || '');
  const [body, setBody] = useState(vendor.existing_body || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!vendor.existing_review_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error('Please select a star rating.'); return; }
    setSaving(true);
    try {
      await submitWeddingReview({ vendor_id: vendor.id, rating, title, body });
      setSaved(true);
      toast.success(`Review for ${vendor.business_name} saved!`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' };

  return (
    <div style={{ background: '#fff', border: `2px solid ${saved ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: '12px', padding: '20px 24px', transition: 'border-color 0.2s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
        {vendor.logo_url || vendor.cover_photo_url ? (
          <img src={vendor.logo_url || vendor.cover_photo_url} alt={vendor.business_name}
            style={{ width: '48px', height: '48px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Heart size={22} color="#be185d" />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong style={{ fontSize: '15px', color: '#1f2937' }}>{vendor.business_name}</strong>
            {saved && <CheckCircle size={16} color="#22c55e" />}
          </div>
          {vendor.category && <span style={{ fontSize: '12px', color: '#6b7280' }}>{vendor.category}</span>}
        </div>
        <a href={`/vendor/${vendor.slug}`} target="_blank" rel="noopener noreferrer"
          style={{ color: '#be185d', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '3px', textDecoration: 'none' }}>
          View profile <ExternalLink size={11} />
        </a>
      </div>

      {saved && !rating ? null : (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
              Your Rating *
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StarPicker value={rating} onChange={setRating} />
              {rating > 0 && (
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#f59e0b' }}>{RATING_LABELS[rating]}</span>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
              Headline <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
            </label>
            <input style={inputStyle} placeholder="Summarise your experience in a few words"
              value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
              Your Review <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea style={{ ...inputStyle, height: '90px', resize: 'vertical' }}
              placeholder="Tell other couples what was great (or could be better). Be specific and honest."
              value={body} onChange={e => setBody(e.target.value)} maxLength={2000} />
            <div style={{ textAlign: 'right', fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>{body.length}/2000</div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button type="submit" className="btn btn-primary" disabled={saving || !rating}
              style={{ padding: '9px 24px', fontSize: '14px' }}>
              {saving ? 'Saving…' : saved ? 'Update Review' : 'Submit Review'}
            </button>
            {saved && (
              <span style={{ fontSize: '13px', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <CheckCircle size={14} /> Review saved
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function WriteReview() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    // Redirecting before the session check resolves bounced signed-in couples
    // straight to /login when they opened a review link directly.
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'couple') { navigate('/'); return; }
    load();
  }, [user, authLoading, navigate]);

  async function load() {
    setLoading(true);
    try {
      const res = await getMyReviewInfo();
      setData(res.data);
      setSavedCount(res.data.vendors.filter(v => v.existing_review_id).length);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load review information');
    } finally {
      setLoading(false);
    }
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : '';

  if (loading) return (
    <div className="page-container" style={{ textAlign: 'center', paddingTop: '80px' }}>
      <div className="spinner"></div>
      <p style={{ color: '#6b7280', marginTop: '12px' }}>Loading your review page…</p>
    </div>
  );

  if (error) return (
    <div className="page-container">
      <div style={{ maxWidth: '560px', margin: '60px auto', textAlign: 'center' }}>
        <Lock size={48} color="#d1d5db" />
        <h2 style={{ color: '#1f2937', marginTop: '16px' }}>Reviews Not Available Yet</h2>
        <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{error}</p>
        <button className="btn btn-primary" onClick={() => navigate('/dashboard')} style={{ marginTop: '20px' }}>
          Back to Dashboard
        </button>
      </div>
    </div>
  );

  const { wedding, vendors } = data;
  const totalVendors = vendors.length;
  const reviewedVendors = vendors.filter(v => v.existing_review_id).length;

  return (
    <div className="page-container" style={{ maxWidth: '720px', margin: '0 auto', padding: '32px 16px 60px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎊</div>
        <h1 style={{ fontSize: '1.8rem', color: '#1f2937', margin: '0 0 6px' }}>
          {wedding.partner1_name}{wedding.partner2_name ? ` & ${wedding.partner2_name}` : ''}
        </h1>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
          {formatDate(wedding.wedding_date)}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '12px',
          background: '#f0fdf4', color: '#15803d', padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: 600, display: 'inline-flex' }}>
          <CheckCircle size={16} /> Wedding Verified Complete
        </div>
      </div>

      {/* Progress */}
      {totalVendors > 0 && (
        <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Reviews submitted</span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#be185d' }}>{reviewedVendors} / {totalVendors}</span>
            </div>
            <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', background: '#be185d', borderRadius: '3px', width: `${totalVendors > 0 ? (reviewedVendors / totalVendors) * 100 : 0}%`, transition: 'width 0.4s' }} />
            </div>
          </div>
          {reviewedVendors === totalVendors && totalVendors > 0 && (
            <span style={{ fontSize: '20px' }}>🎉</span>
          )}
        </div>
      )}

      {/* Intro */}
      <div style={{ background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#374151', lineHeight: '1.6' }}>
          Your honest reviews help thousands of couples in Nepal choose the right vendors for their wedding day.
          Reviews take just a minute and mean a lot to local businesses.
        </p>
      </div>

      {/* Vendor cards */}
      {vendors.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <p>No booked vendors found for your wedding. A vendor appears here once they mark your inquiry as "Booked"; you can check that on My Inquiries.</p>
          <button className="btn btn-outline" onClick={() => navigate('/my-inquiries')} style={{ marginTop: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            View My Inquiries <ChevronRight size={14} />
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {vendors.map(vendor => (
            <VendorReviewCard
              key={vendor.id}
              vendor={vendor}
              onSaved={() => {
                setSavedCount(c => c + 1);
                // Progress and the thank-you panel are computed from data.vendors,
                // which never changed after a save; refetch quietly (no spinner,
                // so the cards keep their state).
                getMyReviewInfo().then(res => setData(res.data)).catch(() => {});
              }}
            />
          ))}
        </div>
      )}

      {reviewedVendors === totalVendors && totalVendors > 0 && (
        <div style={{ textAlign: 'center', marginTop: '28px', padding: '24px', background: '#f0fdf4', borderRadius: '12px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎊</div>
          <h3 style={{ color: '#15803d', margin: '0 0 8px' }}>Thank you for your reviews!</h3>
          <p style={{ color: '#374151', margin: '0 0 16px', fontSize: '14px' }}>
            Your feedback helps couples across Nepal plan their perfect wedding.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </button>
        </div>
      )}
    </div>
  );
}

export default WriteReview;
